import React, { FC } from "react";
import {
  GetImplantItemChoicesQuery,
  GetOrderByUniqueAttributeQuery,
  GetUserProductPreferencesQuery,
  ItemType,
} from "/@/generated/graphql";
import { CreateOrderImplantItemPreferences } from "./CreateOrderImplantItemPreferences";
import { Grid, Space, Stepper } from "@mantine/core";
import { createStyles } from "@mantine/emotion";
import { CreateProductSummarySideBar } from "../CreateProductSummarySideBar";
import { CreateOrderImplantItemAbutmentDetails } from "/@/pages/products/create/implant-item/CreateOrderImplantItemAbutmentDetails";
import { CreateOrderImplantItemImplantReferenceDetails } from "/@/pages/products/create/implant-item/CreateOrderImplantItemImplantReferenceDetails";
import { CreateOrderImplantItemItemDetails } from "./CreateOrderImplantItemItemDetails";
import { CreateImplantItemStep } from "/@/shared/types/orders/createOrder.types";
import { useCreateOrderStore } from "/@/shared/stores/orders/create-store.store";

type CreateOrderImplantItemsProps = {
  product: NonNullable<
    GetOrderByUniqueAttributeQuery["getOrderByUniqueAttribute"]["products"]
  >[0];
  patient: GetOrderByUniqueAttributeQuery["getOrderByUniqueAttribute"]["patient"];
  userProductPreferences: GetUserProductPreferencesQuery;
  implantItemChoicesData: GetImplantItemChoicesQuery;
  order: GetOrderByUniqueAttributeQuery["getOrderByUniqueAttribute"];
  setIsLoading: (isLoading: boolean) => void;
};

export const CreateOrderImplantItems: FC<CreateOrderImplantItemsProps> = ({
  product,
  patient,
  userProductPreferences,
  implantItemChoicesData,
  order,
  setIsLoading,
}) => {
  const createProductStep = useCreateOrderStore(
    state => state.createProductStep
  );
  const { classes } = useStyles();
  const dataContent = () => {
    switch (createProductStep) {
      case CreateImplantItemStep.ImplantReference:
        return (
          <CreateOrderImplantItemImplantReferenceDetails
            product={product}
            implantItemChoicesData={implantItemChoicesData}
            setIsLoading={setIsLoading}
          />
        );
      case CreateImplantItemStep.Abutment:
        return (
          <CreateOrderImplantItemAbutmentDetails
            product={product}
            patient={patient}
            implantItemChoicesData={implantItemChoicesData}
            setIsLoading={setIsLoading}
          />
        );
      case CreateImplantItemStep.Item:
        return (
          <CreateOrderImplantItemItemDetails
            product={product}
            implantItemChoicesData={implantItemChoicesData}
            order={order}
            setIsLoading={setIsLoading}
          />
        );
      case CreateImplantItemStep.Preferences:
        return (
          <CreateOrderImplantItemPreferences
            product={product}
            userProductPreferences={userProductPreferences}
            setIsLoading={setIsLoading}
          />
        );
      default:
        return <div>ERROR create implant item</div>;
    }
  };

  return (
    <Grid columns={36} gap={0} style={{ height: "100%" }}>
      <Grid.Col
        span={createProductStep < CreateImplantItemStep.Review ? 26 : 36}
        style={theme => ({
          padding: theme.spacing.lg,
        })}
      >
        <Stepper
          active={createProductStep}
          classNames={{
            step: classes.stepper,
            stepBody: classes.stepBody,
          }}
        >
          <Stepper.Step label="Implant" />
          <Stepper.Step label="Pilier" />
          <Stepper.Step
            label={
              product.productType === ItemType.BridgeImplant
                ? "Bridge"
                : "Couronne"
            }
          />
          <Stepper.Step label="Préférences" />
        </Stepper>
        <Space h="xl" />
        {dataContent()}
      </Grid.Col>
      {createProductStep !== CreateImplantItemStep.Review && (
        <Grid.Col
          span={10}
          style={theme => ({
            padding: theme.spacing.sm,
            backgroundColor: theme.colors.primaryLight[9],
          })}
        >
          <CreateProductSummarySideBar product={product} patient={patient} />
        </Grid.Col>
      )}
    </Grid>
  );
};

const useStyles = createStyles(theme => ({
  stepper: {
    flexDirection: "column",
  },
  stepBody: {
    marginLeft: 0,
    marginTop: theme.spacing.xs,
    alignItems: "center",
  },
}));
